import type { RouteRecordRaw } from 'vue-router'
import { CAPABILITIES, ROUTES } from './constants'

/**
 * Landlord & Agent workspace routes.
 *
 * Paths come from `constants.ts::ROUTES`, which mirrors `docs/v2/route-map.md` — the contract
 * between phases, so two phases cannot invent different paths for the same screen.
 *
 * Every route carries `meta.workspace = 'landlord'`, so an affiliate who follows one of these
 * links lands on their own dashboard rather than a 403. The capability meta is UX only: the
 * server refuses regardless (architecture.md §3).
 *
 * Routes for later phases — analytics (11) — are **not** declared. Route-map rule 4: never ship
 * navigation that leads to a blank page.
 */
const base = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'landlord' as const,
}

export const landlordRoutes: RouteRecordRaw[] = [
  {
    path: '/app/landlord',
    name: 'landlord-overview',
    component: () => import('./pages/LandlordOverviewPage.vue'),
    meta: { ...base, title: 'landlord.nav.overview' },
  },
  {
    path: '/app/landlord/properties',
    name: 'landlord-properties',
    component: () => import('./pages/LandlordPropertiesPage.vue'),
    meta: {
      ...base,
      capability: CAPABILITIES.propertyView,
      title: 'landlord.nav.properties',
    },
  },
  // `/properties/new` is the onboarding flow — a property is created with its spaces — and is
  // declared by `modules/onboarding/routes.ts`, next to its affiliate twin.
  {
    path: '/app/landlord/properties/:id(\\d+)',
    name: 'landlord-property-detail',
    component: () => import('./pages/LandlordPropertyDetailPage.vue'),
    meta: { ...base, capability: CAPABILITIES.propertyView, title: 'landlord.nav.properties' },
  },
  // `/properties/:id/edit` is the same flow, editing: the property's fields and any spaces to
  // add, declared by `modules/onboarding/routes.ts` next to its affiliate twin.
  {
    path: '/app/landlord/spaces',
    name: 'landlord-spaces',
    component: () => import('./pages/LandlordSpacesPage.vue'),
    meta: { ...base, capability: CAPABILITIES.spaceView, title: 'landlord.nav.spaces' },
  },
  {
    path: '/app/landlord/spaces/:id(\\d+)',
    name: 'landlord-space-detail',
    component: () => import('./pages/LandlordSpaceDetailPage.vue'),
    meta: { ...base, capability: CAPABILITIES.spaceView, title: 'landlord.nav.spaces' },
  },
  // `/tenants`, `/tenants/:id`, `/renewals` and `/payments` are declared by
  // `modules/tenancy/routes.ts` (Phase 07), next to their affiliate twins, so the two cannot drift.
  {
    // Estates are a view of the portfolio page, not a page. The old address still works —
    // it is in the responsive and axe sweeps, and in whatever anyone bookmarked.
    path: '/app/landlord/estates',
    redirect: { path: '/app/landlord/properties', query: { tab: 'estates' } },
  },
  {
    path: '/app/landlord/settings/profile',
    name: 'landlord-settings-profile',
    component: () => import('./pages/LandlordProfilePage.vue'),
    meta: { ...base, title: 'landlord.nav.profile' },
  },
  {
    /**
     * Payout settings live on the profile page's **Account** tab, not on a page of their own.
     * This record stays so that every bookmark, legacy redirect and older link still lands on
     * the settings themselves rather than on a 404.
     */
    path: '/app/landlord/settings/payout',
    name: 'landlord-settings-payout',
    redirect: { path: ROUTES.settingsProfile, query: { tab: 'account' } },
  },
]
